import React, { useEffect, useMemo, useState } from "react";
import {
  FaChalkboardTeacher,
  FaExclamationTriangle,
  FaLock,
  FaPlusCircle,
  FaSearch,
  FaShieldAlt,
  FaUserCheck,
  FaUserCog,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import { useAppTheme } from "../context/AppThemeContext";
import AdminModalShell from "./AdminModalShell";
import { PASSWORD_POLICY_MESSAGE, validatePasswordStrength } from "../utils/passwordPolicy";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const dummyUsers = [
  {
    id: "demo-t-101",
    name: "Rahul Patil",
    email: "rahul.patil@classnx.demo",
    role: "TEACHER",
    department: "Mathematics",
    assignment: "Class 10 A",
    status: "PASSWORD_RESET_REQUIRED",
    active: true,
  },
  {
    id: "demo-t-102",
    name: "Neha Kulkarni",
    email: "neha.kulkarni@classnx.demo",
    role: "TEACHER",
    department: "Science",
    assignment: "Class 9 B",
    status: "ACTIVE",
    active: true,
  },
  {
    id: "demo-s-201",
    name: "Ananya Deshmukh",
    email: "ananya.deshmukh@classnx.demo",
    role: "STUDENT",
    department: "Class 8",
    assignment: "Section A",
    status: "ACTIVE",
    active: true,
  },
  {
    id: "demo-f-301",
    name: "Priya Joshi",
    email: "priya.joshi@classnx.demo",
    role: "STAFF",
    department: "Accounts",
    assignment: "Fee Desk",
    status: "ACTIVE",
    active: true,
  },
  {
    id: "demo-f-302",
    name: "Sanjay Kale",
    email: "sanjay.kale@classnx.demo",
    role: "STAFF",
    department: "Library",
    assignment: "Librarian",
    status: "INACTIVE",
    active: false,
  },
];

const emptyForm = {
  role: "TEACHER",
  name: "",
  email: "",
  password: "",
  department: "",
  assignment: "",
};

const roleOptions = ["ALL", "TEACHER", "STAFF", "STUDENT"];

const normalizeUser = (user = {}, index = 0) => ({
  id: user.id || user.userId || user._id || `user-${index + 1}`,
  name: user.name || user.fullName || user.username || "Unnamed User",
  email: user.email || user.userEmail || "",
  role: String(user.role || user.userType || user.category || "STAFF").toUpperCase(),
  department: user.department || user.subject || user.standard || user.className || "Not assigned",
  assignment: user.assignment || user.section || user.designation || user.classSection || "Pending assignment",
  status: String(user.status || (user.passwordResetRequired ? "PASSWORD_RESET_REQUIRED" : user.active === false ? "INACTIVE" : "ACTIVE")).toUpperCase(),
  active: typeof user.active === "boolean" ? user.active : String(user.status || "").toUpperCase() !== "INACTIVE",
});

const extractUserList = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  return [];
};

const getStatusTone = (status, isDarkMode) => {
  if (status === "PASSWORD_RESET_REQUIRED") {
    return isDarkMode
      ? "border-amber-900/70 bg-amber-950/40 text-amber-200"
      : "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (status === "INACTIVE") {
    return isDarkMode
      ? "border-rose-900/70 bg-rose-950/40 text-rose-200"
      : "border-rose-200 bg-rose-50 text-rose-700";
  }
  return isDarkMode
    ? "border-emerald-900/70 bg-emerald-950/40 text-emerald-200"
    : "border-emerald-200 bg-emerald-50 text-emerald-700";
};

const AdminUserManagement = ({ embedded = false }) => {
  const { isDarkMode } = useAppTheme();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dataSource, setDataSource] = useState("api");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);

      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:users"), () => api.get(API_PATHS.admin.users()), { forceRefresh: refreshVersion > 0 });
        const nextUsers = extractUserList(response.data).map(normalizeUser);

        if (nextUsers.length > 0) {
          setUsers(nextUsers);
          setDataSource("api");
        } else {
          setUsers(dummyUsers);
          setDataSource("dummy");
        }
      } catch (error) {
        setUsers(dummyUsers);
        setDataSource("dummy");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [refreshVersion]);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const matchesRole = roleFilter === "ALL" || user.role === roleFilter;
      const matchesQuery =
        !query
        || user.name.toLowerCase().includes(query)
        || user.email.toLowerCase().includes(query)
        || user.department.toLowerCase().includes(query)
        || user.assignment.toLowerCase().includes(query);

      return matchesRole && matchesQuery;
    });
  }, [roleFilter, searchTerm, users]);

  const summary = useMemo(() => ({
    total: users.length,
    teachers: users.filter((user) => user.role === "TEACHER").length,
    pendingReset: users.filter((user) => user.status === "PASSWORD_RESET_REQUIRED").length,
    inactive: users.filter((user) => !user.active).length,
  }), [users]);

  const handleFormChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const passwordError = validatePasswordStrength(form.password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      role: form.role,
      department: form.department.trim(),
      assignment: form.assignment.trim(),
    };

    try {
      const response = await api.post(API_PATHS.admin.createUser(), payload);
      const createdPayload = response.data?.data ?? response.data ?? payload;
      const createdUser = normalizeUser(
        {
          ...createdPayload,
          ...payload,
          passwordResetRequired: true,
          active: true,
        },
        users.length
      );

      setUsers((current) => [createdUser, ...current]);
      invalidateCache(getTenantCacheKey("admin:users"));
      setDataSource("api");
      toast.success(`${payload.role} created successfully.`);
      setIsCreateModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error?.message || "User could not be created on the server.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserStatus = async (userId, nextActive) => {
    const nextStatus = nextActive ? "ACTIVE" : "INACTIVE";

    try {
      await api.patch(API_PATHS.admin.userStatus(userId), {
        active: nextActive,
        status: nextStatus,
      });
      setUsers((current) => current.map((user) => (
        user.id === userId ? { ...user, active: nextActive, status: nextStatus } : user
      )));
      invalidateCache(getTenantCacheKey("admin:users"));
      toast.success(`User ${nextActive ? "activated" : "deactivated"}.`);
    } catch (error) {
      toast.error(error?.message || "User status could not be changed on the server.");
    }
  };

  return (
    <div className={`${embedded ? "min-h-0 px-1 py-1 md:px-2 md:py-2" : "min-h-screen px-3 py-4 md:px-6 md:py-8"} ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.12),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#ffffff_45%,_#eef2ff_100%)] text-slate-900"}`}>
      <div className="mx-auto max-w-7xl space-y-6">
        <section className={`overflow-hidden rounded-3xl border px-4 py-5 shadow-2xl sm:px-6 sm:py-8 md:px-8 ${isDarkMode ? "border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 shadow-black/30" : "border-white/60 bg-gradient-to-br from-slate-900 via-sky-900 to-cyan-700 text-white shadow-slate-300/40"}`}>
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200">
                Step 1: User Management UI
              </p>
              <h1 className="mt-3 text-2xl font-extrabold sm:text-3xl md:text-5xl">
                Build teachers, staff, and student access from one workspace
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 md:text-base">
                This screen is the first missing MVP block from your SaaS sheet: admin-side user management with temp-password style onboarding and password reset tracking.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
              {[
                { label: "Total users", value: summary.total, icon: FaUsers },
                { label: "Teachers", value: summary.teachers, icon: FaChalkboardTeacher },
                { label: "Pending reset", value: summary.pendingReset, icon: FaLock },
                { label: "Inactive", value: summary.inactive, icon: FaShieldAlt },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">{item.label}</p>
                        <p className="mt-3 text-3xl font-bold text-white">{item.value}</p>
                      </div>
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white">
                        <Icon />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {dataSource === "dummy" && (
          <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? "border-amber-900/70 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Dummy data is being displayed.</p>
                <p className="mt-1 opacity-90">API returned no data or is not ready yet, so this screen is showing demo records to keep UI development moving.</p>
              </div>
            </div>
          </div>
        )}

        <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">User form</p>
                <h2 className="mt-2 text-2xl font-bold">Open user form</h2>
              </div>
              <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                Popup form
              </div>
            </div>
            <p className={`mt-5 text-sm leading-6 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
              Add teacher, staff, or student from a dedicated popup form so the directory stays visible while you create accounts.
            </p>
            <div className={`mt-5 rounded-2xl border p-4 text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              Newly created users are marked as <strong>Password Reset Required</strong> to align this UI with your target onboarding flow.
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
              <ModuleImportButton moduleKey="users" label="Import users" />
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-full bg-cyan-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400"
              >
                <FaPlusCircle /> Create user
              </button>
            </div>
          </div>

          <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Directory</p>
                <h2 className="mt-2 text-2xl font-bold">School users</h2>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative">
                  <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 py-3 pl-10 pr-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100 sm:w-64"
                    placeholder="Search users"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  className="rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                >
                  {roleOptions.map((option) => (
                    <option key={option} value={option}>{option === "ALL" ? "All roles" : option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {loading ? (
                <div className={`rounded-2xl border px-4 py-10 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  Loading user directory...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className={`rounded-2xl border px-4 py-10 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  No users matched the current filters.
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const RoleIcon = user.role === "TEACHER" ? FaChalkboardTeacher : user.role === "STUDENT" ? FaUserGraduate : FaUserCog;
                  return (
                    <div
                      key={user.id}
                      className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex gap-4">
                          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-800 text-cyan-200" : "bg-cyan-50 text-cyan-700"}`}>
                            <RoleIcon />
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-lg font-bold">{user.name}</h3>
                              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getStatusTone(user.status, isDarkMode)}`}>
                                {user.status.replaceAll("_", " ")}
                              </span>
                              {dataSource === "dummy" && (
                                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-white text-slate-500"}`}>
                                  Dummy data
                                </span>
                              )}
                            </div>
                            <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{user.email}</p>
                            <div className={`mt-3 flex flex-wrap gap-2 text-xs ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                              <span className={`rounded-full px-3 py-1 ${isDarkMode ? "bg-slate-800" : "bg-white"}`}>{user.role}</span>
                              <span className={`rounded-full px-3 py-1 ${isDarkMode ? "bg-slate-800" : "bg-white"}`}>{user.department}</span>
                              <span className={`rounded-full px-3 py-1 ${isDarkMode ? "bg-slate-800" : "bg-white"}`}>{user.assignment}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={() => toggleUserStatus(user.id, !user.active)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${user.active ? (isDarkMode ? "bg-rose-950/50 text-rose-200 hover:bg-rose-950/70" : "bg-rose-50 text-rose-700 hover:bg-rose-100") : (isDarkMode ? "bg-emerald-950/50 text-emerald-200 hover:bg-emerald-950/70" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100")}`}
                          >
                            {user.active ? <FaShieldAlt /> : <FaUserCheck />}
                            {user.active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>

        <AdminModalShell
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          isDarkMode={isDarkMode}
          maxWidth="max-w-3xl"
        >
          <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
            <div className="flex items-center justify-between gap-4 pr-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">User form</p>
                <h2 className="mt-2 text-2xl font-bold">Add teacher, staff, or student</h2>
              </div>
              <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                Popup form
              </div>
            </div>

            <form onSubmit={handleCreateUser} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>User role</label>
                  <select
                    value={form.role}
                    onChange={(event) => handleFormChange("role", event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                  >
                    <option value="TEACHER">Teacher</option>
                    <option value="STAFF">Staff</option>
                    <option value="STUDENT">Student</option>
                  </select>
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Full name</label>
                  <input type="text" value={form.name} onChange={(event) => handleFormChange("name", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100" placeholder="Enter full name" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Email</label>
                  <input type="email" value={form.email} onChange={(event) => handleFormChange("email", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100" placeholder="name@school.com" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Temporary password</label>
                  <input type="password" value={form.password} onChange={(event) => handleFormChange("password", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100" placeholder="Set a temporary password" autoComplete="new-password" required />
                  <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{PASSWORD_POLICY_MESSAGE}</p>
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{form.role === "TEACHER" ? "Subject or department" : form.role === "STUDENT" ? "Class" : "Department"}</label>
                  <input type="text" value={form.department} onChange={(event) => handleFormChange("department", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100" placeholder={form.role === "TEACHER" ? "Mathematics" : form.role === "STUDENT" ? "Class 8" : "Accounts"} required />
                </div>
              </div>

              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{form.role === "TEACHER" ? "Class assignment" : form.role === "STUDENT" ? "Section" : "Designation"}</label>
                <input type="text" value={form.assignment} onChange={(event) => handleFormChange("assignment", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100" placeholder={form.role === "TEACHER" ? "Class 10 A" : form.role === "STUDENT" ? "Section A" : "Librarian"} required />
              </div>

              <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                Newly created users are marked as <strong>Password Reset Required</strong> to align this UI with your target onboarding flow.
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={submitting} className="inline-flex items-center rounded-full bg-cyan-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:opacity-70">
                  {submitting ? "Saving..." : "Create user"}
                </button>
                <button type="button" onClick={resetForm} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  Reset
                </button>
              </div>
            </form>
          </div>
        </AdminModalShell>
      </div>
    </div>
  );
};

export default AdminUserManagement;
